import { Injectable, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Attachment, SaveTicketAction, Ticket, TicketType } from 'wms-lib';

@Injectable()
export class TicketAttachmentsBaseComponent implements OnInit {
  ticket: Ticket;
  ticketType: TicketType;
  selected_attachments: Attachment[] = [];

  constructor(private store: Store) {
  }

  ngOnInit() {
    this.store.select(state => state.helpDeskState.active_ticket).subscribe(ticket => {
      this.ticket = ticket;
      this.ticketType = ticket ? ticket.ticket_type : null;
    })
  }

  get canCountFileRows(): boolean {
    return this.ticketType ? this.ticketType.can_count_file_rows : false;
  }

  get acceptedFileTypes(): string { 
    if (!this.ticketType || !this.ticketType.config || !this.ticketType.config.file_restrictions || this.ticketType.config.file_restrictions.length === 0) {
      return '';
    }

    const fileTypes: string[] = [];
    this.currentFileStatus.forEach(status => {
      if (status.current < status.max) {
        status.fileTypes.forEach(type => {
          const ext = `.${type.toLowerCase()}`;
          if (!fileTypes.includes(ext)) {
            fileTypes.push(ext);
          }
        });
      }
    });

    return fileTypes.join(',');
  }

  get currentFileStatus(): { fileTypes: string[]; current: number; min: number; max: number; status: string }[] {
    if (!this.ticketType || !this.ticketType.config || !this.ticketType.config.file_restrictions || this.ticketType.config.file_restrictions.length === 0) {
      return [];
    }

    const existingAttachments = this.ticket && this.ticket.attachments ? 
      this.ticket.attachments.filter(att => !att._destroy) : [];


    const existingFilesByType: { [key: string]: any[] } = {};
    existingAttachments.forEach(attachment => {
      const attachmentAny = attachment as any;
      const namePart = attachmentAny.name ? attachmentAny.name.split('.').pop() : undefined;
      const extension = namePart ? namePart.toLowerCase() : undefined;
      if (extension) {
        if (!existingFilesByType[extension]) {
          existingFilesByType[extension] = [];
        }
        existingFilesByType[extension].push(attachment);
      }
    });


    const status: any[] = [];
    this.ticketType.config.file_restrictions.forEach(restriction => {
      if (restriction.file_types && Array.isArray(restriction.file_types)) {
        let totalCount = 0;
        restriction.file_types.forEach(fileType => {
          const count = existingFilesByType[fileType] ? existingFilesByType[fileType].length || 0 : 0;
          totalCount += count;
        });


        let minQuantity = restriction.quantity_range_start || 1;
        let maxQuantity = restriction.quantity_range_end || 999;

        if (restriction.quantity_range) {
          const parts = restriction.quantity_range.split('..');
          if (parts.length === 2) {
            minQuantity = parseInt(parts[0], 10);
            maxQuantity = parseInt(parts[1], 10);
          }
        }
        
        let statusValue: string = 'ok';
        if (totalCount < minQuantity) {
          statusValue = 'insufficient';
        } else if (totalCount > maxQuantity) {
          statusValue = 'excess';
        }

        status.push({
          fileTypes: restriction.file_types.map(type => type.toUpperCase()),
          current: totalCount,
          min: minQuantity,
          max: maxQuantity,
          status: statusValue
        });
      }
    });

    return status;
  }

  validateFileRestrictions(newFiles: File[], checkMin: boolean = true): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!this.ticketType || !this.ticketType.config || !this.ticketType.config.file_restrictions) {
      return { valid: true, errors: [] };
    }

    const existingAttachments = this.ticket && this.ticket.attachments ? 
      this.ticket.attachments.filter(att => !att._destroy) : [];

    const existingFilesByType: { [key: string]: any[] } = {};
    existingAttachments.forEach(attachment => {
      const attachmentAny = attachment as any;
      const namePart = attachmentAny.name ? attachmentAny.name.split('.').pop() : undefined;
      const extension = namePart ? namePart.toLowerCase() : undefined;
      if (extension) {
        if (!existingFilesByType[extension]) {
          existingFilesByType[extension] = [];
        }
        existingFilesByType[extension].push(attachment);
      }
    });

    const newFilesByType: { [key: string]: File[] } = {};
    newFiles.forEach(file => {
      const fileNamePart = file.name.split('.').pop();
      const extension = fileNamePart ? fileNamePart.toLowerCase() : undefined;
      if (extension) {
        if (!newFilesByType[extension]) {
          newFilesByType[extension] = [];
        }
        newFilesByType[extension].push(file);
      }
    });

    this.ticketType.config.file_restrictions.forEach(restriction => {
      if (restriction.file_types && Array.isArray(restriction.file_types)) {
        let existingTotal = 0;
        let newTotal = 0;
        
        restriction.file_types.forEach(fileType => {
          existingTotal += existingFilesByType[fileType] ? existingFilesByType[fileType].length || 0 : 0;
          newTotal += newFilesByType[fileType] ? newFilesByType[fileType].length || 0 : 0;
        });
        
        const totalCount = existingTotal + newTotal;
        let minQuantity = restriction.quantity_range_start || 1;
        let maxQuantity = restriction.quantity_range_end || 999;

        if (restriction.quantity_range) {
          const parts = restriction.quantity_range.split('..');
          if (parts.length === 2) {
            minQuantity = parseInt(parts[0], 10);
            maxQuantity = parseInt(parts[1], 10);
          }
        }

        const fileTypesStr = restriction.file_types.join(', ').toUpperCase();
        
        if (checkMin && totalCount < minQuantity) {
          errors.push(`${fileTypesStr} files: minimum ${minQuantity} required, found ${totalCount} (${existingTotal} existing + ${newTotal} new)`);
        }
        if (totalCount > maxQuantity) {
          errors.push(`${fileTypesStr} files: maximum ${maxQuantity} allowed, found ${totalCount} (${existingTotal} existing + ${newTotal} new)`);
        }
      }
    });

    return { valid: errors.length === 0, errors };
  }

  deleteAttachment() {
    this.selected_attachments.forEach((a: Attachment) => a._destroy = true);
    this.save();
    this.selected_attachments = [];
  }

  deleteSingleAttachment(attachment: Attachment) {
  	  attachment._destroy = true;
	  this.save();
	  this.selected_attachments = [];
  }

  attachmentTap(attachment: Attachment) {
    this.toggleSelectedAttachment(attachment);
  }

  toggleSelectedAttachment(attachment: Attachment) {
    let att = this.selected(attachment);
    if (att) {
      this.selected_attachments = this.selected_attachments.filter(a => a.id !== attachment.id);
    } else {
      this.selected_attachments.push(attachment);
    }
  }

  selected(attachment: Attachment) {
    return this.selected_attachments.find(a => a.id === attachment.id)
  }

  save() {
    return this.store.dispatch(new SaveTicketAction({ ticket: this.ticket}));
  }
}
